import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { AvailabilityCard, type AvailabilityWindow } from '../components/AvailabilityCard.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { FormField } from '../components/FormField.tsx';
import { MyCalendarsPanel } from '../components/MyCalendarsPanel.tsx';
import { MyTeamsLicensePanel } from '../components/MyTeamsLicensePanel.tsx';
import { useToast } from '../components/Toaster.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface FullAgent {
  id: string;
  email: string;
  name: string;
  role: string;
  calendarBookingUrl: string | null;
}

export function AgentProfilePage(): React.ReactElement {
  useDocumentTitle('My profile');
  const { agent, refresh } = useAuth();
  const toast = useToast();
  const [me, setMe] = useState<FullAgent | null>(null);
  const [name, setName] = useState('');
  const [calendarUrl, setCalendarUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [availability, setAvailability] = useState<AvailabilityWindow[]>([]);

  useEffect(() => {
    apiFetch<{ agent: FullAgent }>('/api/auth/agent/me/full')
      .then((r) => {
        setMe(r.agent);
        setName(r.agent.name);
        setCalendarUrl(r.agent.calendarBookingUrl ?? '');
        // Pull the agent's availability from the per-agent endpoint so
        // the My-profile view always matches what other techs see.
        apiFetch<{ availability: AvailabilityWindow[] }>(`/api/agents/${r.agent.id}`)
          .then((res) => setAvailability(res.availability))
          .catch(() => undefined);
      })
      .catch(() => undefined);
  }, []);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      await apiFetch('/api/auth/agent/me', {
        method: 'PATCH',
        json: {
          name: name.trim(),
          calendarBookingUrl: calendarUrl.trim() || null,
        },
      });
      toast.success('Profile updated.');
      await refresh();
      const r = await apiFetch<{ agent: FullAgent }>('/api/auth/agent/me/full');
      setMe(r.agent);
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Save failed.');
    } finally {
      setBusy(false);
    }
  }

  if (!agent) return <p>Loading…</p>;

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div>
        <Link to="/dashboard" className="text-sm text-[var(--color-muted)] hover:underline">
          ← Dashboard
        </Link>
        <h1 className="mt-1 text-2xl font-bold text-[var(--color-header)]">My profile</h1>
      </div>

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Account</h2>
        <dl className="mt-3 grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Email</dt>
            <dd>{me?.email ?? agent.email}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-[var(--color-muted)]">Role</dt>
            <dd className="capitalize">{me?.role ?? agent.role}</dd>
          </div>
        </dl>
      </Card>

      {me && (
        <AvailabilityCard
          agentId={me.id}
          initial={availability}
          canEdit
          onSaved={(w) => setAvailability(w)}
        />
      )}

      <MyCalendarsPanel />

      <MyTeamsLicensePanel />

      <Card>
        <h2 className="text-base font-semibold text-[var(--color-header)]">Personal calendar booking link</h2>
        <p className="mt-1 text-sm text-[var(--color-muted)]">
          Paste your booking URL (Calendly, Google Appointment Schedule, Cal.com, etc.). When a
          ticket is assigned to you, clients will see a <strong>Schedule a session</strong> button on
          the ticket that opens this link.
        </p>
        <form onSubmit={save} className="mt-3 space-y-3">
          <FormField
            label="Display name"
            name="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <FormField
            label="Booking URL"
            name="calendarUrl"
            type="url"
            placeholder="https://calendly.com/your-handle"
            value={calendarUrl}
            onChange={(e) => setCalendarUrl(e.target.value)}
            hint="Leave blank to hide the Schedule button on tickets assigned to you."
          />
          <Button type="submit" disabled={busy}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
